import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Link,
  Paper,
} from '@mui/material';
import CustomButton from '../../../components/MasterComponent/Button/CustomButton';
import {
  config,
  extractAccountIdFromArn,
  triggerAwsJob,
} from './ManualSections/config';
import { ManualArnProps } from '../../../schemas/account.schema';
import { useFetchData } from '../../../hooks/useFetchData';
import { AccountForm } from '../../../components/AccountOnboarding/AccountForm';
import Loading from '../../../components/Loading/Loading';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useHistory } from 'react-router-dom';
import { RoutesFun } from '../../../schemas';

interface AutomatedCardProps {
  onCancel: () => void;
}

const AutomatedCard: React.FC<AutomatedCardProps> = ({ onCancel }) => {
  const [awsArn, setAwsArn] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  // const [iamRoleArn, setIamRoleArn] = useState('');
  const { fetch, data, loader: isSubmitting, error } = useFetchData({
    fetchData: (formData: ManualArnProps) =>
      config.addManualArnHandler(formData),
    fetchOnMount: false,
  });

  const errorMessage =
  error?.error?.message ||
  'There was an issue connecting your AWS account to CloudForestX.';

  const history = useHistory();

  const handleSubmit = async () => {
    try {
      if (isFormValid) {
        const payload: ManualArnProps = {
          AccountArn: awsArn,
          Name: companyName,
        };

        await fetch(payload);

        const accountId = extractAccountIdFromArn(awsArn);

        if (accountId) {
          triggerAwsJob(accountId);
        }
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Automated Onboarding
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Run a CloudFormation template to onboard the AWS account
      </Typography>

      <Box mt={2}>
        <List>
          <ListItem sx={{ alignItems: 'flex-start' }}>
            <ListItemIcon>
              <Box className="stepNumberBadge">1</Box>
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="h5">
                  Log in to the AWS account and run this{' '}
                  <Link
                    href="https://us-east-1.console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/create/review?stackName=CFX-Client-Integration&templateURL=https://cfx-cross-account-access.s3.us-west-2.amazonaws.com/cfx-onboarding-template-final.yaml"
                    underline="hover"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {' '}
                    CloudFormation Template
                  </Link>
                  , click on <strong>Create Stack</strong>.
                </Typography>
              }
            />
          </ListItem>
          <ListItem sx={{ alignItems: 'flex-start' }}>
            <ListItemIcon>
              <Box className="stepNumberBadge">2</Box>
            </ListItemIcon>
            <ListItemText>
              <Typography variant="h5" className="mb-30">
                Once the CloudFormation template execution completes, go to the{' '}
                <strong> Outputs</strong> tab and copy the IAM Role ARN. Paste
                the copied Role ARN below -
              </Typography>
              {isSubmitting ? (
                <Paper
                  elevation={1}
                  sx={{ height: '28rem' }}
                  className="formPaper"
                >
                  <Loading />
                </Paper>
              ) : data || error ? (
                <Box
                  className={`accountCompletionContainer ${
                    error ? 'error' : 'success'
                  }`}
                >
                  {error ? (
                    <CancelOutlinedIcon className="statusIcon" />
                  ) : (
                    <CheckCircleOutlineIcon className="statusIcon" />
                  )}
                  <Typography variant="h4" className="statusTitle">
                    {error ? 'Account Setup Failed!' : 'Account Setup Complete!'}
                  </Typography>
                  <Typography variant="body2" className="statusMessage">
                    {error
                      ? errorMessage
                      : 'Your CloudForestX onboarding has been successfully completed! You will be able to view our resources and services within 24 hours, and detailed AWS cost breakdowns will be available within 24 hours.'}
                        </Typography>

                        {!error && (
                          <CustomButton
                            buttonType="contained"
                            onClickHandler={() => history.push(RoutesFun().Dashboard)}
                            style={{ marginTop: '1rem' }}
                          >
                            Go To Dashboard
                          </CustomButton>
                        )}
                      </Box>
              ) : (
                <AccountForm
                  onAwsArnChange={setAwsArn}
                  onCompanyNameChange={setCompanyName}
                  onFormValidityChange={setIsFormValid}
                />
              )}
              {/* <Box mt={3}>
                <Typography variant="body2" gutterBottom>
                  Enter the IAM Role ARN <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Grid sm={6} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Enter the IAM Role ARN"
                    value={iamRoleArn}
                    size="small"
                    onChange={(e) => setIamRoleArn(e.target.value)}
                  />
                </Grid>
              </Box> */}
            </ListItemText>
          </ListItem>
        </List>
      </Box>

      <Box mt={3} display="flex" justifyContent="space-between">
        <CustomButton
          onClickHandler={() => history.push(RoutesFun().CreateAccount)}
          buttonType="outlined"
        >
          CANCEL
        </CustomButton>
        {(data || !error) && (!data || error) && (
          <CustomButton
            buttonType="contained"
            onClickHandler={handleSubmit}
            disabled={!isFormValid}
          >
            Complete Onboarding
          </CustomButton>
        )}
      </Box>
    </Box>
  );
};

export default AutomatedCard;
